import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  hämtaIntagsfel, försökIgenIntagsfel, markeraIntagsfelHanterat, Intagsfel,
} from '../api/client';
import { TEMA } from '../theme';

const kortStyle: React.CSSProperties = {
  background: TEMA.bgPanel,
  border: `1px solid ${TEMA.border}`,
  borderRadius: '8px',
  padding: '18px 22px',
  marginBottom: '12px',
};

const sekundärKnapp: React.CSSProperties = {
  height: '36px',
  padding: '0 18px',
  borderRadius: '2px',
  background: 'transparent',
  border: `1px solid ${TEMA.border}`,
  color: TEMA.inkSoft,
  fontFamily: "'Inter', sans-serif",
  fontSize: '13.5px',
  cursor: 'pointer',
};

// Vad ett nytt försök ledde till, i klartext.
const UTFALL: Record<string, string> = {
  lyckad: 'Registrerat och kopplat till kunden.',
  delvis_kopplad: 'Registrerat. Kontrollera inköparen på kunden.',
  okopplad: 'Registrerat, men behöver kopplas till en kund under Aktiviteter att koppla.',
  uppgift: 'Sparat som påminnelse i Att göra.',
  underlag: 'Mötesunderlaget har skickats.',
  dubblett: 'Mejlet var redan registrerat, så inget nytt skapades.',
  ignorerad: 'Automatisk avsändare, hoppades över.',
  misslyckad: 'Misslyckades igen. Den nya orsaken står i listan.',
};

function tidText(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('sv-SE', { day: 'numeric', month: 'short' }) + ' ' +
    d.toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' });
}

export default function Inlasningsfel() {
  const [rader, setRader] = useState<Intagsfel[]>([]);
  const [laddar, setLaddar] = useState(true);
  const [fel, setFel] = useState<string | null>(null);
  const [arbetar, setArbetar] = useState<string | null>(null);
  const [öppen, setÖppen] = useState<string | null>(null);
  const [besked, setBesked] = useState<{ text: string; tillKoppla: boolean } | null>(null);

  async function ladda() {
    const r = await hämtaIntagsfel();
    setRader(r.data);
  }

  useEffect(() => {
    ladda()
      .catch(err => {
        console.error('Kunde inte ladda mejlen:', err);
        setFel('Kunde inte ladda listan just nu. Försök igen om en stund.');
      })
      .finally(() => setLaddar(false));
  }, []);

  async function försökIgen(r: Intagsfel) {
    setArbetar(r.id);
    setBesked(null);
    try {
      const { data } = await försökIgenIntagsfel(r.id);
      setBesked({
        text: UTFALL[data.status] || `Inläst på nytt: ${data.status}.`,
        tillKoppla: data.status === 'okopplad',
      });
      await ladda();
    } catch (err: any) {
      console.error('Kunde inte läsa in igen:', err);
      alert(err?.response?.data?.detail || 'Kunde inte läsa in mejlet igen. Försök igen om en stund.');
    } finally {
      setArbetar(null);
    }
  }

  async function hanterad(r: Intagsfel) {
    setArbetar(r.id);
    try {
      await markeraIntagsfelHanterat(r.id);
      setRader(prev => prev.filter(x => x.id !== r.id));
    } catch (err: any) {
      console.error('Kunde inte spara:', err);
      alert(err?.response?.data?.detail || 'Kunde inte spara. Försök igen.');
    } finally {
      setArbetar(null);
    }
  }

  if (laddar) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '40vh' }}>
      <p style={{ color: TEMA.inkFaint, fontSize: '14px', fontFamily: "'Inter', sans-serif" }}>Laddar...</p>
    </div>
  );

  if (fel) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '40vh' }}>
      <p style={{ color: '#D9695F', fontSize: '14px', fontFamily: "'Inter', sans-serif" }}>{fel}</p>
    </div>
  );

  return (
    <div>
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ margin: 0, fontFamily: "'Fraunces', serif", fontSize: '30px', color: TEMA.ink }}>Mejl som inte lästes in</h1>
        <p style={{ margin: '10px 0 0', fontSize: '13.5px', color: TEMA.inkSoft, fontFamily: "'Inter', sans-serif", maxWidth: '680px', lineHeight: 1.5 }}>
          Mejl till mötesadressen som inte blev någon aktivitet, de senaste 30 dagarna. Försök igen
          när orsaken är åtgärdad, till exempel när avsändaren lagts upp som användare. Markera som
          hanterat när mötet registrerats för hand eller mejlet inte var ett möte.
        </p>
      </div>

      {besked && (
        <div style={{ ...kortStyle, display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'center' }}>
          <span style={{ fontSize: '13.5px', color: TEMA.ink, fontFamily: "'Inter', sans-serif" }}>
            {besked.text}
            {besked.tillKoppla && <> <Link to="/att-koppla" style={{ color: TEMA.accent, textDecoration: 'none' }}>Öppna Aktiviteter att koppla →</Link></>}
          </span>
          <button onClick={() => setBesked(null)} style={{ ...sekundärKnapp, height: '28px', padding: '0 12px' }}>Stäng</button>
        </div>
      )}

      {rader.length === 0 && (
        <div style={{ ...kortStyle, textAlign: 'center', padding: '40px 22px' }}>
          <p style={{ margin: 0, fontSize: '14px', color: TEMA.inkFaint, fontFamily: "'Inter', sans-serif" }}>
            Alla mötesmejl har lästs in.
          </p>
        </div>
      )}

      {rader.map(r => {
        const upptagen = arbetar === r.id;
        const visas = öppen === r.id;
        return (
          <div key={r.id} style={kortStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'baseline', flexWrap: 'wrap' }}>
              <span style={{ fontFamily: "'Fraunces', serif", fontSize: '16px', color: TEMA.ink }}>
                {r.avsandare || 'Okänd avsändare'}
              </span>
              <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: '12px', color: TEMA.inkFaint }}>
                {tidText(r.tid)}
              </span>
            </div>
            <p style={{ margin: '6px 0 0', fontSize: '13.5px', color: TEMA.inkSoft, fontFamily: "'Inter', sans-serif" }}>
              {r.orsak.charAt(0).toUpperCase() + r.orsak.slice(1)}
              {r.mottagare && <span style={{ color: TEMA.inkFaint }}> · till {r.mottagare}</span>}
            </p>

            {r.text && (
              <button
                onClick={() => setÖppen(visas ? null : r.id)}
                style={{
                  background: 'none', border: 'none', padding: 0, marginTop: '8px', cursor: 'pointer',
                  color: TEMA.accent, fontFamily: "'Inter', sans-serif", fontSize: '12.5px',
                }}
              >
                {visas ? 'Dölj mejlet' : 'Visa mejlet'}
              </button>
            )}
            {visas && (
              <pre style={{
                margin: '10px 0 0', padding: '12px 14px', background: TEMA.bg, borderRadius: '4px',
                border: `1px solid ${TEMA.border}`, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                maxHeight: '320px', overflowY: 'auto',
                fontFamily: "'Inter', sans-serif", fontSize: '12.5px', lineHeight: 1.5, color: TEMA.inkSoft,
              }}>
                {r.text}
              </pre>
            )}

            <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap', marginTop: '14px' }}>
              {r.kan_forsokas && (
                <button
                  onClick={() => försökIgen(r)}
                  disabled={upptagen}
                  style={{
                    height: '36px', padding: '0 18px', borderRadius: '2px',
                    background: TEMA.accent, color: TEMA.onAccent, border: `1px solid ${TEMA.accent}`,
                    fontFamily: "'Inter', sans-serif", fontSize: '13.5px', fontWeight: 500,
                    cursor: upptagen ? 'default' : 'pointer', opacity: upptagen ? 0.6 : 1,
                  }}
                >
                  {upptagen ? 'Läser in...' : 'Försök igen'}
                </button>
              )}
              <button onClick={() => hanterad(r)} disabled={upptagen} style={sekundärKnapp}>
                Markera som hanterat
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
